/**
 * Convenience facade that turns a per-route description into the JSON-LD
 * `scripts` entries to spread into TanStack Start's `head().scripts`.
 *
 * Goal: keep route files ~one extra line of schema wiring.
 */

import {
  asJsonLd,
  blogListSchema,
  blogPostingSchema,
  breadcrumbSchema,
  collectionPageSchema,
  contactPageSchema,
  faqSchema,
  type Locale,
  serviceSchema,
  type ServiceSchemaInput,
  softwareAppSchema,
  SERVICE_CATALOG,
  SITE_ORIGIN,
  urlFor,
  websiteSchema,
  type BreadcrumbItem,
} from "./schema";
import { reportSchema, reportListSchema } from "./schema";
import { extractFaqPairs } from "./faq-from-html";

/** Section labels for auto-built breadcrumbs. */
const SECTION_LABELS: Record<string, { en: string; bg: string }> = {
  "media-monitoring": {
    en: "Media monitoring",
    bg: "Медиен мониторинг",
  },
  "media-analysis": {
    en: "Media analysis",
    bg: "Медиен анализ",
  },
  "who-we-help": {
    en: "Who we help",
    bg: "На кого помагаме",
  },
  industries: {
    en: "Industries",
    bg: "Индустрии",
  },
  "about-us": { en: "About us", bg: "За нас" },
  blog: { en: "Blog", bg: "Блог" },
  reports: { en: "Reports", bg: "Доклади" },
  pricing: { en: "Pricing", bg: "Цени" },
  careers: { en: "Careers", bg: "Кариери" },
  "contact-us": { en: "Contact us", bg: "Свържете се с нас" },
  "request-a-demo": { en: "Request a demo", bg: "Заявете демо" },
  "why-perceptica": { en: "Why Perceptica", bg: "Защо Perceptica" },
  platform: { en: "Platform", bg: "Платформа" },
  coverage: { en: "Coverage", bg: "Покритие" },
  reputation: { en: "Reputation analysis", bg: "Анализ на репутацията" },
  "competitive-intelligence": {
    en: "Competitive intelligence",
    bg: "Конкурентно разузнаване",
  },
  "crisis-monitoring": {
    en: "Crisis monitoring",
    bg: "Кризисен мониторинг",
  },
  "employer-branding": {
    en: "Employer branding",
    bg: "Анализ на работодателски бранд",
  },
  "market-research": { en: "Market research", bg: "Пазарно проучване" },
  faq: { en: "FAQ", bg: "Често задавани въпроси" },
  "brand-managers": { en: "Brand managers", bg: "Бранд мениджъри" },
  "pr-managers": { en: "PR managers", bg: "PR мениджъри" },
  "pr-agency": { en: "PR agencies", bg: "PR агенции" },
  "media-agencies": { en: "Media agencies", bg: "Медийни агенции" },
  "employer-branding-experts": {
    en: "Employer branding experts",
    bg: "Експерти по работодателски бранд",
  },
  automotive: { en: "Automotive", bg: "Автомобилна индустрия" },
  fintech: { en: "Fintech", bg: "Финансова индустрия" },
  fmcg: { en: "FMCG", bg: "FMCG" },
  pharma: { en: "Pharma", bg: "Фармацевтична индустрия" },
  retail: { en: "Retail", bg: "Търговия на дребно" },
  "cookie-policy": { en: "Cookie policy", bg: "Политика за бисквитки" },
  "privacy-policy": { en: "Privacy policy", bg: "Политика за поверителност" },
  partners: { en: "Partners & Press", bg: "Партньори и медии" },
};

function labelFor(seg: string, locale: Locale): string {
  const e = SECTION_LABELS[seg];
  if (e) return e[locale];
  // Fallback: title-case the slug.
  return seg
    .split("-")
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

/** Auto-build a breadcrumb trail (excluding Home) from a language-agnostic path. */
function trailFromPath(locale: Locale, path: string): BreadcrumbItem[] {
  if (path === "/" || path === "") return [];
  const segs = path.replace(/^\//, "").split("/").filter(Boolean);
  const out: BreadcrumbItem[] = [];
  let acc = "";
  for (const s of segs) {
    acc += `/${s}`;
    out.push({ name: labelFor(s, locale), path: acc });
  }
  return out;
}

type ServiceInput = Omit<ServiceSchemaInput, "url" | "locale">;

/** Strip a trailing "/index" so route paths line up with canonical URLs. */
function normalizePath(path: string): string {
  return path.replace(/\/index$/, "") || "/";
}

/**
 * Sector (industry) and role (who-we-help) pages are audience variants of the
 * core media intelligence offering, so they get a Service node automatically.
 */
function variantService(locale: Locale, path: string): ServiceInput | undefined {
  const segs = normalizePath(path).replace(/^\//, "").split("/");
  if (segs.length !== 2) return undefined;
  const [section, slug] = segs;
  const label = labelFor(slug, locale);
  if (section === "industries") {
    return {
      name:
        locale === "bg"
          ? `Медиен мониторинг и анализ за ${label}`
          : `Media monitoring and analysis for ${label}`,
      description:
        locale === "bg"
          ? `Медиен мониторинг, медиен анализ и пазарни проучвания от Perceptica за ${label} в Югоизточна Европа.`
          : `Perceptica media monitoring, media analysis and market research for the ${label} sector across Southeast Europe.`,
      serviceType: locale === "bg" ? "Медийно разузнаване" : "Media intelligence",
      category: locale === "bg" ? "Медиен анализ" : "Media analysis",
      serviceOutput: locale === "bg" ? "Секторен медиен анализ" : "Sector media analysis report",
      audience: { industry: label },
      isSimilarTo: [`${SITE_ORIGIN}/media-analysis`, `${SITE_ORIGIN}/media-monitoring`],
    };
  }
  if (section === "who-we-help") {
    return {
      name: locale === "bg" ? `Медийно разузнаване за ${label}` : `Media intelligence for ${label}`,
      description:
        locale === "bg"
          ? `Медиен мониторинг и анализи от Perceptica, съобразени с ежедневната работа на ${label}.`
          : `Perceptica media monitoring and analysis built around the day-to-day work of ${label}.`,
      serviceType: locale === "bg" ? "Медийно разузнаване" : "Media intelligence",
      category: locale === "bg" ? "Медиен мониторинг" : "Media monitoring",
      serviceOutput:
        locale === "bg" ? "Медиен отчет и табло" : "Media intelligence reporting and dashboards",
      audience: { role: label },
      isSimilarTo: [`${SITE_ORIGIN}/media-monitoring`, `${SITE_ORIGIN}/media-analysis`],
    };
  }
  return undefined;
}

/** Fill in serviceOutput + sibling links from the sitewide service catalogue. */
function withCatalogDefaults(path: string, service: ServiceInput): ServiceInput {
  const p = normalizePath(path);
  const entry = SERVICE_CATALOG.find((s) => s.path === p);
  if (!entry) return service;
  return {
    ...service,
    serviceOutput: service.serviceOutput ?? entry.serviceOutput,
    isSimilarTo:
      service.isSimilarTo ??
      SERVICE_CATALOG.filter((s) => s.path !== p)
        .slice(0, 4)
        .map((s) => `${SITE_ORIGIN}${s.path}`),
  };
}

export interface PageSchemasInput {
  locale: Locale;
  /** Language-agnostic path with leading slash (e.g. "/media-monitoring/platform"). */
  path: string;
  /** Override trail when auto-derivation is wrong. */
  trail?: BreadcrumbItem[];
  /** Emit Service schema with these inputs (URL auto-filled from path/locale). */
  service?: Omit<ServiceSchemaInput, "url" | "locale">;
  /** Emit SoftwareApplication schema for the platform pages. */
  software?: boolean;
  /** Emit ContactPage schema. */
  contact?: boolean;
  /** Emit CollectionPage schema. */
  collection?: {
    name: string;
    description: string;
    hasPart?: { name: string; path: string }[];
  };
  /** Emit FAQPage schema with these Q&A items. */
  faq?: { q: string; a: string }[];
}

/**
 * Build the JSON-LD scripts array for a page. Always includes a BreadcrumbList
 * (unless the page is the root), plus any optional schemas requested.
 */
export function pageSchemas(input: PageSchemasInput) {
  const url = urlFor(input.locale, normalizePath(input.path));
  const trail = input.trail ?? trailFromPath(input.locale, input.path);

  const nodes: Array<Record<string, unknown> | undefined | false | null> = [];

  if (trail.length > 0) {
    nodes.push(breadcrumbSchema(input.locale, trail));
  }

  const service = input.service ?? variantService(input.locale, input.path);
  if (service) {
    nodes.push(
      serviceSchema({
        ...withCatalogDefaults(input.path, service),
        locale: input.locale,
        url,
      }),
    );
  }

  if (input.software) {
    nodes.push(softwareAppSchema(input.locale, url));
  }

  if (input.contact) {
    nodes.push(contactPageSchema(input.locale, url));
  }

  if (input.collection) {
    nodes.push(
      collectionPageSchema({
        locale: input.locale,
        url,
        name: input.collection.name,
        description: input.collection.description,
        hasPart: input.collection.hasPart?.map((p) => ({
          name: p.name,
          url: urlFor(input.locale, p.path),
        })),
      }),
    );
  }

  if (input.faq && input.faq.length > 0) {
    nodes.push(faqSchema(input.faq, input.locale));
  }

  return asJsonLd(...nodes);
}

const BLOG_LABEL = { en: "Blog", bg: "Блог" } as const;

/**
 * JSON-LD for the blog list pages (/blog and /bg/blog):
 * WebSite + Blog + BreadcrumbList, in that order.
 */
export function blogListSchemas(input: {
  locale: Locale;
  name: string;
  description: string;
  posts?: { title: string; slug: string; datePublished?: string }[];
}) {
  const url = urlFor(input.locale, "/blog");
  return asJsonLd(
    websiteSchema(),
    blogListSchema({
      locale: input.locale,
      url,
      name: input.name,
      description: input.description,
      posts: input.posts?.map((p) => ({
        name: p.title,
        url: urlFor(input.locale, `/blog/${p.slug}`),
        datePublished: p.datePublished,
      })),
    }),
    breadcrumbSchema(input.locale, [{ name: BLOG_LABEL[input.locale], path: "/blog" }]),
  );
}

/**
 * JSON-LD for a single blog post page:
 * WebSite + BlogPosting + BreadcrumbList, in that order.
 * The breadcrumb always uses the canonical slug so duplicates don't create
 * conflicting trails.
 */
export function blogPostSchemas(input: {
  locale: Locale;
  canonicalSlug: string;
  title: string;
  description: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  authorName?: string;
  /** Rendered article HTML; Q&A sections become FAQPage JSON-LD. */
  contentHtml?: string;
}) {
  const url = urlFor(input.locale, `/blog/${input.canonicalSlug}`);
  const faq = extractFaqPairs(input.contentHtml);
  return asJsonLd(
    websiteSchema(),
    blogPostingSchema({
      locale: input.locale,
      url,
      headline: input.title,
      description: input.description,
      image: input.image,
      datePublished: input.datePublished,
      dateModified: input.dateModified,
      authorName: input.authorName,
    }),
    breadcrumbSchema(input.locale, [
      { name: BLOG_LABEL[input.locale], path: "/blog" },
      { name: input.title, path: `/blog/${input.canonicalSlug}` },
    ]),
    ...(faq.length ? [faqSchema(faq, input.locale)] : []),
  );
}

const REPORTS_LABEL = { en: "Reports", bg: "Доклади" } as const;

/**
 * JSON-LD for a single report landing page:
 * WebSite + Article/Report (with DigitalDocument + associatedMedia PDF,
 * research-desk author, ISO dates and gating signals) + BreadcrumbList.
 */
export function reportSchemas(input: {
  locale: Locale;
  slug: string;
  title: string;
  description: string;
  image?: string;
  year?: string;
  dateModified?: string;
  topic?: string;
  pdfUrl?: string;
  datePublished?: string;
  analyst?: { name: string; role?: string };
}) {
  const url = urlFor(input.locale, `/reports/${input.slug}`);
  return asJsonLd(
    websiteSchema(),
    reportSchema({
      locale: input.locale,
      url,
      title: input.title,
      description: input.description,
      image: input.image,
      year: input.year,
      dateModified: input.dateModified,
      topic: input.topic,
      pdfUrl: input.pdfUrl,
      datePublished: input.datePublished,
      analyst: input.analyst,
    }),
    breadcrumbSchema(input.locale, [
      { name: REPORTS_LABEL[input.locale], path: "/reports" },
      { name: input.title, path: `/reports/${input.slug}` },
    ]),
  );
}

/** ItemList of all reports, appended to the /reports index schema graph. */
export function reportListSchemas(input: {
  locale: Locale;
  reports: { title: string; slug: string; year?: string }[];
}) {
  const url = urlFor(input.locale, "/reports");
  return asJsonLd(
    websiteSchema(),
    reportListSchema({
      locale: input.locale,
      url,
      reports: input.reports.map((r) => ({
        name: r.title,
        url: urlFor(input.locale, `/reports/${r.slug}`),
        year: r.year,
      })),
    }),
  );
}